import { useEffect, useRef, useState } from 'react';

export interface MenuCoords {
  top?: number;
  bottom?: number;
  right: number;
}

/**
 * Позиционирование выпадающего меню «три точки», которое рендерится порталом
 * в body: внутри плитки или `.table-wrap` (overflow) дропдаун обрезался бы
 * либо растягивал блок, добавляя прокрутку.
 *
 * itemCount – число пунктов: по нему считаем высоту меню и решаем, открывать
 * вниз или вверх, если до низа экрана места не хватает.
 */
export function useMenuPortal(itemCount: number) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const toggle = () => {
    if (!open && triggerRef.current) {
      // Пункт меню ~34px плюс вертикальные отступы контейнера.
      const menuHeight = itemCount * 34 + 16;
      const rect = triggerRef.current.getBoundingClientRect();
      const right = document.documentElement.clientWidth - rect.right;
      setCoords(
        window.innerHeight - rect.bottom < menuHeight
          ? { bottom: window.innerHeight - rect.top + 4, right }
          : { top: rect.bottom + 4, right },
      );
    }
    setOpen((v) => !v);
  };

  /** Закрыть меню и выполнить действие пункта. */
  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  return { open, coords, triggerRef, dropdownRef, toggle, run, close: () => setOpen(false) };
}
